import {
  DestinationInterface,
  DispatchGuideDto,
  DispatchInterface,
  DispatchResponseInterface,
  DispatcherCCWInterface,
} from '../interfaces'
import axios from 'axios'
import {
  ListResponse,
  SimpleResponse,
} from '../interfaces/Shipment/ListResponseDto'
import { store } from '../store/store'
import { apiDjango, unwrap } from './ApiDjango'
import {
  ManifestByShipmentDto,
  ManifestByShipmentRequest,
  MANIFEST_BY_SHIPMENT_MAX,
} from '../interfaces/Django/ManifestByShipmentDto'
// Este archivo habla con los DOS backends: .NET para el manifiesto en sí, Django para
// la consulta de manifiestos por guía.
const { REACT_APP_URL_PATH_API, REACT_APP_URL_PATH_API_DJANGO } = process.env

/**
 * El manifiesto manda una línea POR PIEZA, no por guía: 5000 guías de 3 piezas
 * son 15.000 líneas en un solo POST. El axios global no define timeout, así que
 * sin esto la petición cuelga hasta el default del navegador y el operador no
 * sabe si su manifiesto se creó. Ver `docs` del plan: "timeout ambiguo".
 */
const CREATE_MANIFEST_TIMEOUT_MS = 120000

/**
 * Traduce un error de axios a un `ListResponse`/`SimpleResponse` con `didError`
 * EXPLÍCITO.
 *
 * Antes, las tres funciones de este archivo hacían `return error.response`, y eso
 * fallaba de dos formas distintas:
 *
 *   1. Red caída o timeout → `error.response` es `undefined` → se devolvía `{}` o
 *      un array vacío. `didError` quedaba `undefined`.
 *   2. 4xx/5xx CON cuerpo → se devolvía el AxiosResponse entero (`{status, data,
 *      headers, ...}`), no el cuerpo. `didError` vivía en `.data.didError`, así
 *      que en el objeto devuelto también quedaba `undefined`.
 *
 * En ambos casos el llamador evalúa `!response?.didError` → `!undefined` → `true`
 * → **trata el error como éxito**. En `getGuideDispatchs` eso pintaba la tabla
 * vacía con el mensaje "Parece que aún no tienes preguías" a un cliente que
 * acababa de cargar 1500 guías. En `saveDispatchs`, mostraba pantalla de éxito
 * con el número de manifiesto vacío.
 *
 * Hermano del learning `getlockbuservices-fail-open`.
 */
const toFailure = <T,>(error: any, fallbackMessage: string): ListResponse<T> => {
  const body = error?.response?.data

  // 4xx/5xx con un envelope válido del backend: se respeta su mensaje.
  if (body && typeof body === 'object' && 'didError' in body) {
    return { ...(body as ListResponse<T>), didError: true }
  }

  const message =
    (typeof body === 'string' && body) ||
    body?.message ||
    error?.message ||
    fallbackMessage

  return {
    didError: true,
    message,
    errorMessage: message,
    model: null,
  }
}

export const getAllDispatchers = async (
  accountCode?: string,
  accountId?: string
): Promise<ListResponse<DispatcherCCWInterface[]> | undefined> => {
  try {
    const response = await axios.post(
      `${REACT_APP_URL_PATH_API}/v1/Dispatchers/GetAllCCW`,
      {
        CodCustomer: accountCode,
        AccountId: accountId,
      }
    )
    return response.data as ListResponse<DispatcherCCWInterface[]>
  } catch (error: any) {
    return toFailure<DispatcherCCWInterface[]>(
      error,
      'No se pudieron consultar los despachos.'
    )
  }
}

export const getGuideDispatchs = async (
  accountCode: string,
  accountId: string,
  dateFrom?: string,
  dateTo?: string
): Promise<ListResponse<DispatchGuideDto[]> | undefined> => {
  try {
    const response = await axios.post(
      `${REACT_APP_URL_PATH_API}/v1/Dispatchers/GetAllMissingCCW`,
      {
        CodCustomer: accountCode,
        AccountID: accountId,
        DateFrom: dateFrom,
        DateTo: dateTo,
      }
    )
    return response.data as ListResponse<DispatchGuideDto[]>
  } catch (error: any) {
    return toFailure<DispatchGuideDto[]>(
      error,
      'No se pudieron consultar las guías pendientes de despacho.'
    )
  }
}

export const saveDispatchs = async (
  dispatch: DispatchInterface
): Promise<SimpleResponse<DispatchResponseInterface> | undefined> => {
  try {
    const response = await axios.post(
      `${REACT_APP_URL_PATH_API}/Manifiest/CreateManifest`,
      dispatch,
      { timeout: CREATE_MANIFEST_TIMEOUT_MS }
    )
    return response.data as SimpleResponse<DispatchResponseInterface>
  } catch (error: any) {
    return toFailure<DispatchResponseInterface>(
      error,
      'No se pudo crear el manifiesto.'
    ) as SimpleResponse<DispatchResponseInterface>
  }
}

/**
 * ¿El manifiesto se creó de verdad?
 *
 * No alcanza con `didError === false`: un timeout puede dejar la petición sin
 * respuesta habiendo creado el manifiesto igual, y una respuesta OK sin código
 * no sirve para nada (no se puede imprimir ni marcar la carga masiva).
 *
 *   didError === false  &&  hay código   →  ÉXITO
 *   didError === true                    →  FALLO
 *   cualquier otra cosa                  →  INDETERMINADO
 *
 * El caso INDETERMINADO NO se puede tratar como fallo: el manifiesto puede
 * existir. Hay que mandar al operador a Consulta de manifiestos antes de que
 * reintente, o termina con dos manifiestos para el mismo camión.
 */
export type ManifestSaveOutcome = 'created' | 'failed' | 'unknown'

export const manifestSaveOutcome = (
  response: SimpleResponse<DispatchResponseInterface> | undefined
): ManifestSaveOutcome => {
  const code = response?.model?.masterShipmentHeaderCode
  if (response?.didError === false && !!code) return 'created'
  if (response?.didError === true) return 'failed'
  return 'unknown'
}

export const getDestinationByCode = async (
  code: string
): Promise<DestinationInterface | undefined> => {
  //console.log(`[API CALL] GET Account Contact by code: ${code}`)
  return undefined
}

export const DowloadDispatch = async (
  dispatchNumber: string,
  type: string,
  userPrint: string
): Promise<any> => {
  const { token } = store.getState().inmutable
  const requestOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  }

  return fetch(
    `${REACT_APP_URL_PATH_API}/manifiest/DownloadManifiest?ManifestCode=${dispatchNumber}&type=${type}&UserPrint=${userPrint}`,
    requestOptions
  )
}

// ---------------------------------------------------------------------------
// Manifiestos por guía — POST /api/manifest/by-shipment (Django)
//
// El resto de este archivo va contra el API .NET; este endpoint vive en Django, así
// que usa `apiDjango` + `unwrap` (el patrón para endpoints Django). Se agrupa acá y
// no en un archivo nuevo porque el dominio es el mismo: manifiestos.
// ---------------------------------------------------------------------------

/**
 * Para cada guía, en qué manifiesto está.
 *
 * Contesta la pregunta que el operador se hace cuando abre el modal y sus guías no
 * están en la lista: "¿dónde fueron a parar?". Casi siempre ya están manifestadas.
 *
 * Parte la consulta en tandas de MANIFEST_BY_SHIPMENT_MAX porque el servidor rechaza
 * listas más largas, y una carga masiva puede traer 5000+ guías. Las tandas van en
 * SERIE, no en paralelo: son consultas de fondo para un aviso informativo, y disparar
 * N peticiones pesadas a la vez compite con lo que el operador sí está esperando.
 *
 * NO lanza: devuelve `didError` y el modal degrada al aviso genérico. Importa porque
 * al escribir esto el endpoint todavía no estaba desplegado — hasta que lo esté, esta
 * llamada falla y el modal tiene que seguir funcionando igual.
 */
export const getManifestsByShipment = async (
  shipmentNumbers: string[],
  filters?: Omit<ManifestByShipmentRequest, 'shipment_numbers'>
): Promise<ListResponse<ManifestByShipmentDto[]>> => {
  const clean = Array.from(
    new Set(shipmentNumbers.map((n) => (n ?? '').trim()).filter(Boolean))
  )
  if (clean.length === 0) {
    return { model: [], didError: false, message: '', errorMessage: '' }
  }

  const out: ManifestByShipmentDto[] = []
  for (let i = 0; i < clean.length; i += MANIFEST_BY_SHIPMENT_MAX) {
    const batch = clean.slice(i, i + MANIFEST_BY_SHIPMENT_MAX)
    // eslint-disable-next-line no-await-in-loop
    const res = await unwrap<ManifestByShipmentDto[]>(
      // URL completa, igual que el resto de las llamadas Django del proyecto: se ve
      // tal cual en la pestaña Red y no depende del baseURL de la instancia.
      apiDjango.post(`${REACT_APP_URL_PATH_API_DJANGO}/manifest/by-shipment`, {
        shipment_numbers: batch,
        origin_store: filters?.origin_store ?? null,
        destination_store: filters?.destination_store ?? null,
        manifest_type_id: filters?.manifest_type_id ?? null,
      } as ManifestByShipmentRequest)
    )
    if (res.didError) {
      // 404 (ruta inexistente) y 405 (esa ruta no acepta POST) significan lo mismo
      // acá: el endpoint todavía no está desplegado. Es una condición temporal y
      // esperada, no un fallo del operador — se dice así y no con un "Request
      // failed with status code 405".
      const notDeployed = res.httpStatus === 404 || res.httpStatus === 405
      const msg = notDeployed
        ? 'la consulta de manifiestos por guía todavía no está disponible en este ambiente'
        : res.errorMessage || 'No se pudieron consultar los manifiestos.'
      return { model: null, didError: true, message: msg, errorMessage: msg }
    }
    out.push(...(Array.isArray(res.model) ? res.model : []))
  }

  return { model: out, didError: false, message: '', errorMessage: '' }
}
